<template>
  <VDialog v-model="show" @update:model-value="emit('update:modelValue', $event)" max-width="640">
    <VCard class="p-4">
      <VCardTitle class="flex justify-space-between items-center p-0">
        <div class="text-h4 p-4 pb-2">
          {{ t('Logout') }}
        </div>

        <VBtn
          icon="close"
          variant="plain"
          class="button-small mr-2"
          size="small"
          :ripple="false"
          data-testid="btn-close"
          @click="close"
        />
      </VCardTitle>

      <VCardItem class="p-2 pt-0">
        <div
          v-if="userConsultationsLoading"
          class="flex justify-center items-center min-h-[240px] p-6"
        >
          <LoaderElement type="circular" size="large" />
        </div>

        <VCardText v-else class="p-0">
          <div
            v-if="shouldDisplaySummary"
            data-testid="summary-section"
            class="flex flex-col gap-2 text-base"
          >
            <p>{{ t('Session Summary') }}:</p>

            <ul class="flex flex-col gap-2 list-[square] pl-8">
              <li v-if="!isShiftEnded && isShiftActive" data-testid="active-shift-info">
                <span class="font-semibold">{{ t('Active shift') }}</span>
                <span>
                  {{
                    ` ${t('until {end}. You can receive consultations requests until then', {
                      end: endOfShiftTime
                    })}`
                  }}.
                </span>
              </li>

              <li
                v-if="incomingDirectConsultations.length"
                data-testid="incoming-consultations-info"
              >
                <span class="font-semibold">{{ incomingConsultationsRequestText ?? 0 }}</span>
                <span>{{ ` ${t('awaiting action')}` }}</span>
              </li>

              <li
                v-if="outgoingDirectConsultations.length && outgoingConsultationsRequestText"
                data-testid="outgoing-consultations-info"
              >
                <span class="font-semibold">{{ outgoingConsultationsRequestText[0] }}</span>
                <span>{{ ` ${outgoingConsultationsRequestText[1]}` }}</span>
              </li>
            </ul>

            <div
              v-if="
                !hasGodModeShift &&
                (incomingDirectConsultations.length || outgoingDirectConsultations.length)
              "
              class="flex flex-col gap-2"
              data-testid="handover-section"
            >
              <p>
                {{
                  t(
                    'Your work items will remain active after you log out, unless you hand them over'
                  )
                }}.
              </p>

              <VBtn
                data-testid="logout-handover-button"
                :text="t('Start hand over')"
                variant="text"
                class="flex self-start font-semibold text-primary text-base p-0"
                @click="goToHandover"
              />
            </div>
          </div>

          <p v-else data-testid="confirm-question">
            {{ t('Are you sure you want to log out?') }}
          </p>
        </VCardText>

        <VCardActions class="p-0 pt-4">
          <div class="flex justify-end items-center gap-3 w-full">
            <VBtn
              :text="t('Cancel')"
              variant="text"
              class="button-large font-semibold text-gray-600 text-base"
              @click="close"
            />

            <VBtn
              data-testid="logout-button"
              variant="flat"
              color="primary"
              class="button-large"
              size="large"
              @click="logout()"
            >
              {{ t('Log out of this device') }}
            </VBtn>
          </div>
        </VCardActions>
      </VCardItem>
    </VCard>
  </VDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { isToday } from 'date-fns'
import { useRouter } from 'vue-router'

import { AuthenticationService } from '@/services/AuthenticationService'
import { PushNotificationService } from '@/services/PushNotificationService'
import { UserService } from '@/services/telehealthApi/UserService'
import { ConsultationService } from '@/services/telehealthApi/ConsultationService'
import { routeNames } from '@/router/routeNames'
import { useUserStore } from '@/stores/user'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { useFormatDate } from '@/composables/useFormatDate'
import { useShift } from '@/composables/useShift'
import LoaderElement from '@/atoms/LoaderElement.vue'
import type { ConsultationRequestDTO } from '@/generated'
import { isOwnConsultation } from '@/util/supervisedConsultations'

const show = defineModel<boolean>({ required: true })

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const noticeStore = useNoticeStore()

const { hasGodModeShift, endOfShiftDate } = storeToRefs(userStore)
const { formatDateTime, formatTime } = useFormatDate()
const { isShiftEnded, isShiftActive } = useShift()

const outgoingDirectConsultations = ref<ConsultationRequestDTO[]>([])
const incomingDirectConsultations = ref<ConsultationRequestDTO[]>([])
const userConsultationsLoading = ref(false)

const shouldDisplaySummary = computed(
  () =>
    outgoingDirectConsultations.value.length > 0 ||
    incomingDirectConsultations.value.length > 0 ||
    (!isShiftEnded.value && isShiftActive.value)
)

const endOfShiftTime = computed<string>(() => {
  if (!endOfShiftDate.value) {
    return '–'
  }

  return isToday(endOfShiftDate.value)
    ? formatTime(endOfShiftDate.value)
    : formatDateTime(endOfShiftDate.value)
})

const incomingConsultationsRequestText = computed<string | null>(() => {
  const count = incomingDirectConsultations.value.length

  if (count === 1) {
    return t('1 received consultation')
  }

  if (count > 1) {
    return t('{count} received consultations', count)
  }

  return null
})

const outgoingConsultationsRequestText = computed<[string, string] | null>(() => {
  const count = outgoingDirectConsultations.value.length

  if (count === 1) {
    return [t('1 sent consultation'), t('still open')]
  }

  if (count > 1) {
    return [t('{count} sent consultations', count), t('still open - plural')]
  }

  return null
})

watch(
  () => show.value,
  async (isOpen) => {
    if (isOpen) {
      await fetchUserDirectConsultations()
    }
  },
  { immediate: true }
)

function close() {
  emit('update:modelValue', false)
}

async function goToHandover() {
  close()
  await router.push({ name: routeNames.LOGOUT_HANDOVER })
}

async function fetchUserDirectConsultations(): Promise<void> {
  userConsultationsLoading.value = true

  try {
    outgoingDirectConsultations.value = (
      await ConsultationService.getOutgoingConsultationRequests()
    ).consultationRequests.filter(isOwnConsultation)

    incomingDirectConsultations.value = (
      await ConsultationService.getIncomingConsultationRequests()
    ).consultationRequests.filter(isOwnConsultation)
  } catch (error) {
    console.error((error as Error).message)
  } finally {
    userConsultationsLoading.value = false
  }
}

async function logout(withHandover: boolean = false): Promise<void> {
  try {
    if (hasGodModeShift.value || withHandover) {
      await UserService.handoverShift(userStore.user!.userName, {
        to: undefined,
        handoverConsultationRequests: undefined,
        deleteConsultationRequests: undefined
      })
    }

    await PushNotificationService.deactivateNotificationReception({ preserveUserPreference: true })
    close()
    await AuthenticationService.logout()
  } catch (error) {
    noticeStore.add({
      type: NoticeType.ERROR,
      message: t('There are incoming consultations that must be answered before logging out.')
    })
  }
}
</script>
